import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faPause,
  faVolumeUp,
  faHistory,
  faVolumeDown,
  faVolumeMute
} from '@fortawesome/free-solid-svg-icons';
import { usePlayer } from '../../hooks/usePlayer';
import HistoryModal from './HistoryModal';

const VerticalPlayer = styled.div`
  width: ${props => props.dimensions?.width || '300px'};
  height: ${props => props.dimensions?.height || 'auto'};
  min-height: min-content; /* Ensure content is always visible */
  background: ${props => props.colors.background || 'rgba(0, 0, 0, 0.8)'};
  border-radius: ${props => props.dimensions?.borderRadius || '8px'};
  border: ${props => props.dimensions?.borderSize || '0px'} solid ${props => props.colors.borderColor || 'transparent'};
  padding: ${props => props.dimensions?.margin || '10px'};
  margin: ${props => props.dimensions?.padding || '15px'};
  display: flex;
  flex-direction: column;
  font-family: 'Poppins', sans-serif;
  ${props => props.layoutStyle === 'horizontal' && `
    gap: 0;
  `}
  ${props => props.layoutStyle === 'square' && `
    aspect-ratio: 1;
    height: ${props.dimensions?.width || '300px'};
    padding: ${props.dimensions?.margin || '10px'};
    position: relative;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  `}
  color: ${props => props.colors.text || 'white'};
  overflow: ${props => props.layoutStyle === 'square' ? 'hidden' : 'visible'};
  box-sizing: border-box;
  box-shadow: ${props => {
    if (!props.colors.backgroundShadow) return 'none';
    const angle = props.colors.backgroundShadowAngle || 135;
    const blur = props.colors.backgroundShadowBlur || 15;
    const offset = props.colors.backgroundShadowOffset || 4;
    const angleRad = (angle * Math.PI) / 180;
    const x = Math.cos(angleRad) * offset;
    const y = Math.sin(angleRad) * offset;
    return `${x}px ${y}px ${blur}px ${props.colors.backgroundShadow}`;
  }};
`;

const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 15px;
  font-family: 'Poppins', sans-serif;
`;

const AlbumArtworkContainer = styled.div`
  ${props => props.layoutStyle === 'horizontal' && `
    position: relative;
    width: 120px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  `}
`;

const AlbumArtwork = styled.div`
  width: ${props => props.layoutStyle === 'horizontal' ? '120px' : '100%'};
  aspect-ratio: 1;
  background: rgba(51, 51, 51, 0.2);
  display: ${props => props.settings?.albumArt === 'disable' ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  font-weight: bold;
  text-align: center;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
  ${props => props.layoutStyle === 'horizontal' && `
    flex-shrink: 0;
  `}
  ${props => props.layoutStyle === 'square' && `
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: ${props.dimensions?.borderRadius || '8px'};
    z-index: 1;
    
    &:after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(180deg, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.7) 100%);
      z-index: 2;
    }

    img {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
      z-index: 1;
    }
  `}
`;

const TimeBarContainer = styled.div`
  width: 100%;
  padding: 5px 0;
  ${props => props.layoutStyle === 'square' && `
    position: absolute;
    bottom: 0;
    left: 0;
    padding: 0;
    z-index: 3;
  `}
  ${props => props.layoutStyle === 'horizontal' && props.albumArtDisabled && `
    margin-top: auto;
  `}
  ${props => props.layoutStyle === 'horizontal' && !props.albumArtDisabled && `
    width: 120px;
    padding: 0;
  `}
`;

const TimeBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${props => props.colors.timeBar ? `${props.colors.timeBar}33` : 'transparent'};
  position: relative;
  border-radius: ${props => props.layoutStyle === 'square' ? '0' : '2px'};
  overflow: hidden;
`;

const TimeBarProgress = styled.div`
  height: 100%;
  background: ${props => props.colors.timeBar || '#333333'};
  width: ${props => props.progress}%;
  border-radius: ${props => props.layoutStyle === 'square' ? '0' : '2px'};
`;

const TimeInfo = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 5px 0;
  font-family: 'Poppins', sans-serif;
  font-size: ${props => `${props.fontSize.value * 0.85}${props.fontSize.unit}` || '0.75rem'};
  font-weight: ${props => props.fontWeight || '400'};
  color: ${props => props.colors.text || 'white'};
  ${props => props.layoutStyle === 'square' && `
    position: absolute;
    bottom: 4px;
    left: 0;
    width: 100%;
    padding: 0 10px;
    z-index: 4;
  `}
  ${props => props.layoutStyle === 'horizontal' && props.albumArtDisabled && `
    margin-bottom: 5px;
  `}
  ${props => props.layoutStyle === 'horizontal' && !props.albumArtDisabled && `
    width: 120px;
  `}
`;

const StationInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 5px 0;
  font-family: 'Poppins', sans-serif;
  background: transparent;
  ${props => props.layoutStyle === 'square' && `
    padding: 20px;
    z-index: 3;
  `}
  ${props => props.layoutStyle === 'horizontal' && `
    padding: 0 0 10px 0;
  `}
`;

const RadioName = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: ${props => `${props.fontSize.value * 0.85}${props.fontSize.unit}` || '0.75rem'};
  font-weight: ${props => props.fontWeight || '400'};
  color: ${props => props.colors.text || 'white'};
  letter-spacing: ${props => `${props.fontSpacing?.letterSpacing.value}${props.fontSpacing?.letterSpacing.unit}`};
  line-height: ${props => `${props.fontSpacing?.lineHeight.value}${props.fontSpacing?.lineHeight.unit}`};
  word-spacing: ${props => `${props.fontSpacing?.wordSpacing.value}${props.fontSpacing?.wordSpacing.unit}`};
  ${props => props.layoutStyle === 'square' && `
    font-size: ${props.fontSize.value}${props.fontSize.unit};
    font-weight: 500;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  `}
`;

const LiveContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  ${props => props.layoutStyle === 'square' && `
    background: rgba(0, 0, 0, 0.3);
    padding: 4px 8px;
    border-radius: 12px;
    backdrop-filter: blur(4px);
  `}
`;

const LiveIndicator = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.colors.liveIndicator || '#FF0000'};
  animation: pulse 2s infinite;

  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
`;

const LiveText = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: ${props => `${props.fontSize.value * 0.85}${props.fontSize.unit}` || '0.75rem'};
  font-weight: ${props => props.fontWeight || '400'};
  color: ${props => props.colors.text || 'white'};
  letter-spacing: ${props => `${props.fontSpacing?.letterSpacing.value}${props.fontSpacing?.letterSpacing.unit}`};
  line-height: ${props => `${props.fontSpacing?.lineHeight.value}${props.fontSpacing?.lineHeight.unit}`};
  word-spacing: ${props => `${props.fontSpacing?.wordSpacing.value}${props.fontSpacing?.wordSpacing.unit}`};
  ${props => props.layoutStyle === 'square' && `
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  `}
`;

const SongInfo = styled.div`
  width: 100%;
  padding: 15px 0;
  font-family: 'Poppins', sans-serif;
  text-align: ${props => props.textAlign};
  ${props => props.layoutStyle === 'square' && `
    padding: 0;
    margin-bottom: 15px;
  `}
  ${props => props.layoutStyle === 'horizontal' && `
    padding: 0;
    flex: 1;
  `}
`;

const SongName = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: ${props => `${props.fontSize.value}${props.fontSize.unit}` || '0.875rem'};
  font-weight: ${props => props.fontWeight || 'bold'};
  margin-bottom: 5px;
  text-align: ${props => props.textAlign};
  color: ${props => props.colors.text || 'white'};
  letter-spacing: ${props => `${props.fontSpacing?.letterSpacing.value}${props.fontSpacing?.letterSpacing.unit}`};
  line-height: ${props => `${props.fontSpacing?.lineHeight.value}${props.fontSpacing?.lineHeight.unit}`};
  word-spacing: ${props => `${props.fontSpacing?.wordSpacing.value}${props.fontSpacing?.wordSpacing.unit}`};
`;

const ArtistName = styled.div`
  font-family: 'Poppins', sans-serif;
  font-size: ${props => `${props.fontSize.value * 0.85}${props.fontSize.unit}` || '0.75rem'};
  font-weight: ${props => props.fontWeight || '400'};
  opacity: 0.8;
  text-align: ${props => props.textAlign};
  color: ${props => props.colors.text || 'white'};
  letter-spacing: ${props => `${props.fontSpacing?.letterSpacing.value}${props.fontSpacing?.letterSpacing.unit}`};
  line-height: ${props => `${props.fontSpacing?.lineHeight.value}${props.fontSpacing?.lineHeight.unit}`};
  word-spacing: ${props => `${props.fontSpacing?.wordSpacing.value}${props.fontSpacing?.wordSpacing.unit}`};
`;

const VolumeContainer = styled.div`
  position: absolute;
  bottom: 100%;
  right: 50%;
  transform: translateX(50%);
  padding: 20px;
  border-radius: 4px;
  display: ${props => props.isVisible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  width: 60px;
  height: 120px;
  background: transparent;
  cursor: default;

  &:before {
    content: '';
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid transparent;
  }
`;

const VolumeSlider = styled.input.attrs({ type: 'range', min: 0, max: 100 })`
  width: 100px;
  height: 4px;
  border-radius: 2px;
  background: ${props => props.colors.timeBar || '#4a4a4a'};
  outline: none;
  opacity: 1;
  -webkit-appearance: none;
  transform: rotate(-90deg);
  transform-origin: center;
  position: absolute;
  top: 50%;
  left: 70%;
  margin: 0;
  padding: 10px 0;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${props => props.colors.accent || '#ffffff'};
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    position: relative;
    transform: translateY(-50%);
    top: 50%;

    &:hover {
      transform: translateY(-50%) scale(1.1);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    }
  }

  &::-moz-range-thumb {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: ${props => props.colors.accent || '#ffffff'};
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    transition: all 0.2s ease;
    position: relative;
    transform: translateY(-50%);
    top: 50%;

    &:hover {
      transform: translateY(-50%) scale(1.1);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
    }
  }

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: ${props => props.colors.timeBar || '#4a4a4a'};
  }

  &::-moz-range-track {
    width: 100%;
    height: 4px;
    border-radius: 2px;
    background: ${props => props.colors.timeBar || '#4a4a4a'};
  }
`;

const ControlsContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${props => {
    if (props.buttonAlign === 'left') return 'flex-start';
    if (props.buttonAlign === 'right') return 'flex-end';
    return 'center'; // default center alignment
  }};
  gap: 30px;
  padding: 15px 20px;
  ${props => props.layoutStyle === 'square' && `
    padding: 0;
    margin-bottom: 0;
  `}
  ${props => props.layoutStyle === 'horizontal' && `
    padding: 0;
    margin-bottom: 0;
  `}
`;

const ControlButton = styled.button`
  font-family: 'Poppins', sans-serif;
  background: ${props => (props.buttonStyle === 'text' || props.buttonStyle === 'icons') ? 'transparent' : props.colors.buttonBackground || 'none'};
  border: none;
  color: ${props => props.colors.icon || 'white'};
  cursor: pointer;
  padding: ${props => (props.buttonStyle === 'text' || props.buttonStyle === 'icons') ? '0.25rem' : '0.5rem'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.isPlay ? '1.5rem' : '1rem'};
  box-shadow: ${props => {
    if (!props.colors.buttonShadow || props.buttonStyle === 'text' || props.buttonStyle === 'icons') return 'none';
    const angle = props.colors.buttonShadowAngle || 135;
    const blur = props.colors.buttonShadowBlur || 4;
    const offset = props.colors.buttonShadowOffset || 2;
    const angleRad = (angle * Math.PI) / 180;
    const x = Math.cos(angleRad) * offset;
    const y = Math.sin(angleRad) * offset;
    return `${x}px ${y}px ${blur}px ${props.colors.buttonShadow}`;
  }};
  border-radius: ${props => {
    switch (props.buttonStyle) {
      case 'circle': return '50%';
      case 'square': return '0';
      case 'rounded': return '4px';
      case 'text':
      case 'icons':
        return '0';
      default: return '50%';
    }
  }};
  width: ${props => {
    if (props.buttonStyle === 'text' || props.buttonStyle === 'icons') return 'auto';
    // Base size for non-play buttons is 36px
    const baseSize = 36;
    // Play button is 30% larger
    return props.isPlay ? `${baseSize * 1.3}px` : `${baseSize}px`;
  }};
  height: ${props => {
    if (props.buttonStyle === 'text' || props.buttonStyle === 'icons') return 'auto';
    // Base size for non-play buttons is 36px
    const baseSize = 36;
    // Play button is 30% larger
    return props.isPlay ? `${baseSize * 1.3}px` : `${baseSize}px`;
  }};
  aspect-ratio: ${props => (props.buttonStyle === 'circle' || props.buttonStyle === 'square') ? '1' : 'auto'};
  transition: color 0.1s ease;
  
  &:hover {
    color: ${props => props.colors.accent || props.colors.icon || 'white'};
  }

  .fa-play {
    margin-left: 2px;
    font-size: ${props => props.isPlay ? '1.5rem' : '1rem'};
  }

  .fa-pause {
    font-size: ${props => props.isPlay ? '1.5rem' : '1rem'};
  }
`;

const PlayerContent = styled.div`
  font-family: 'Poppins', sans-serif;
  display: flex;
  flex-direction: column;
  ${props => props.layoutStyle === 'horizontal' ? `
    flex: 1;
    width: auto;
    min-width: 0;
    margin-left: ${props.albumArtDisabled ? '0' : '15px'};
    height: 120px;
    display: flex;
    flex-direction: column;
  ` : props.layoutStyle === 'square' ? `
    position: absolute;
    bottom: 80px;
    left: 0;
    width: 100%;
    z-index: 3;
    padding: 0 20px;
    gap: 15px;
  ` : `
    width: 100%;
  `}
`;

const StickyFooterPlayer = styled(VerticalPlayer)`
    flex-direction: row;
    align-items: center;
    gap: 20px;
    padding: ${props => props.dimensions?.padding || '15px 20px'};
    width: 100%;
    height: ${props => props.dimensions?.height || '100px'};
    position: fixed;
    bottom: 0;
    left: 0;
    margin: 0;
    border-radius: 0;
    border: ${props => props.dimensions?.borderSize || '0px'} solid ${props => props.colors.borderColor || 'transparent'};
    z-index: 9999;
    background: ${props => props.colors.background || 'rgba(0, 0, 0, 0.8)'};
    box-shadow: ${props => {
    if (!props.colors.backgroundShadow) return 'none';
    const angle = props.colors.backgroundShadowAngle || 135;
    const blur = props.colors.backgroundShadowBlur || 15;
    const offset = props.colors.backgroundShadowOffset || 4;
    const angleRad = (angle * Math.PI) / 180;
    const x = Math.cos(angleRad) * offset;
    const y = Math.sin(angleRad) * offset;
    return `${x}px ${y}px ${blur}px ${props.colors.backgroundShadow}`;
  }};
`;

const StickyFooterControls = styled.div`
    display: flex;
    align-items: center;
    gap: 15px;
    justify-content: ${props => {
    if (props.buttonAlign === 'left') return 'flex-start';
    if (props.buttonAlign === 'right') return 'flex-end';
    return 'center';
  }};
`;

const StickyFooterSongInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-width: 0;
    text-align: ${props => props.textAlign || 'left'};
`;

const StickyFooterTimeInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const StickyFooterTimeBar = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 4px;
`;

const StickyCompactPlayer = styled(VerticalPlayer)`
    display: grid;
    grid-template-columns: auto 1fr auto;
    grid-template-rows: auto auto auto auto;
    gap: 5px;
    padding: ${props => props.dimensions?.padding || '15px 15px 5px 15px'};
    width: ${props => props.dimensions?.width || '400px'};
    height: ${props => props.dimensions?.height || 'auto'};
    max-width: 100%;
    background: ${props => props.colors.background || 'rgba(0, 0, 0, 0.8)'};
    position: fixed;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
    z-index: 9999;
    border: ${props => props.dimensions?.borderSize || '0px'} solid ${props => props.colors.borderColor || 'transparent'};
    box-shadow: ${props => {
    if (!props.colors.backgroundShadow) return 'none';
    const angle = props.colors.backgroundShadowAngle || 135;
    const blur = props.colors.backgroundShadowBlur || 15;
    const offset = props.colors.backgroundShadowOffset || 4;
    const angleRad = (angle * Math.PI) / 180;
    const x = Math.cos(angleRad) * offset;
    const y = Math.sin(angleRad) * offset;
    return `${x}px ${y}px ${blur}px ${props.colors.backgroundShadow}`;
  }};
    border-radius: ${props => props.dimensions?.borderRadius || '8px'};
`;

const CompactAlbumArt = styled(AlbumArtwork)`
    width: 90px;
    height: 90px;
    grid-row: 1 / span 3;
    margin-right: 5px;
`;

const CompactHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    grid-column: 2 / span 2;
    gap: 5px;
    text-align: ${props => props.textAlign || 'left'};
    padding-top: 5px;
`;

const CompactSongInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    grid-column: 2;
    text-align: ${props => props.textAlign || 'left'};
    padding: 5px 0;
`;

const CompactControls = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    grid-column: 3;
    justify-self: ${props => {
    if (props.buttonAlign === 'left') return 'start';
    if (props.buttonAlign === 'right') return 'end';
    return 'center';
  }};
`;

const CompactTimeInfo = styled(TimeInfo)`
    justify-content: space-between;
    width: 100%;
    grid-column: 1 / -1;
    padding: 0 10px;
    margin: 0;
    margin-top: 2px;
`;

const CompactTimeBar = styled(TimeBar)`
    width: 100%;
    grid-column: 1 / -1;
    height: 3px;
    margin: 0;
    margin-top: 2px;
`;

const StickyFooterAlbumArt = styled(AlbumArtwork)`
    width: 63px;
    height: 63px;
    border-radius: 4px;
    overflow: hidden;
`;

const PlayerPreview = ({ background, widgetState }) => {
  const { settings = {}, colors = {}, textAlign = 'center', buttonAlign = 'center', urls = {}, albumArt, buttonStyle, dimensions = {}, style = 'vertical', fontSize = { value: 0.875, unit: 'rem' }, fontWeight = '400', fontSpacing = {
    letterSpacing: { value: 0, unit: 'px' },
    lineHeight: { value: 1.5, unit: 'em' },
    wordSpacing: { value: 0, unit: 'px' }
  } } = widgetState || {};
  const {
    isPlaying,
    volume,
    currentSong,
    timeProgress,
    error,
    controls,
    elapsed,
    metadata
  } = usePlayer(urls.streamUrl, urls.apiUrl);

  const [showVolumeBar, setShowVolumeBar] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  useEffect(() => {
    if (metadata) {
      window.postMessage({ type: 'metadata', metadata }, '*');
    }
  }, [metadata]);

  const handleVolumeChange = (e) => {
    const newVolume = parseInt(e.target.value);
    controls.setVolume(newVolume / 100);
  };

  const toggleMute = () => {
    if (volume > 0) {
      controls.setVolume(0);
    } else {
      controls.setVolume(1);
    }
  };

  const getVolumeIcon = () => {
    if (volume === 0) return faVolumeMute;
    if (volume < 0.5) return faVolumeDown;
    return faVolumeUp;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (style === 'footer') {
    return (
      <StickyFooterPlayer colors={colors} dimensions={dimensions}>
        <StickyFooterControls buttonAlign={buttonAlign}>
          <ControlButton
            colors={colors}
            isPlay
            buttonStyle={buttonStyle}
            onClick={controls.togglePlay}
          >
            <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
          </ControlButton>
          {settings.volumeControl && (
            <ControlsContainer>
              <ControlButton
                colors={colors}
                buttonStyle={buttonStyle}
                onClick={() => setShowVolumeBar(!showVolumeBar)}
              >
                <FontAwesomeIcon icon={getVolumeIcon()} />
              </ControlButton>
              <VolumeContainer
                colors={colors}
                isVisible={showVolumeBar}
                onMouseLeave={() => setShowVolumeBar(false)}
              >
                <VolumeSlider
                  colors={colors}
                  value={volume * 100}
                  onChange={handleVolumeChange}
                />
              </VolumeContainer>
            </ControlsContainer>
          )}
          {settings.songHistory && (
            <ControlButton
              colors={colors}
              buttonStyle={buttonStyle}
              onClick={() => setShowHistoryModal(true)}
            >
              <FontAwesomeIcon icon={faHistory} />
            </ControlButton>
          )}
        </StickyFooterControls>

        {albumArt !== 'disable' && (
          <StickyFooterAlbumArt settings={{ albumArt }} dimensions={dimensions}>
            {currentSong?.artwork ? (
              <img
                src={currentSong.artwork}
                alt="Album Art"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: albumArt
                }}
              />
            ) : (
              'ALBUM ARTWORK AREA'
            )}
          </StickyFooterAlbumArt>
        )}

        <StickyFooterSongInfo textAlign={textAlign}>
          {settings.songName && (
            <SongName colors={colors} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing} textAlign={textAlign}>
              {currentSong?.title || 'Song Name'}
            </SongName>
          )}
          {settings.artistName && (
            <ArtistName colors={colors} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing} textAlign={textAlign}>
              {currentSong?.artist || 'Artist Name'}
            </ArtistName>
          )}
        </StickyFooterSongInfo>

        <StickyFooterTimeInfo>
          {settings.time && (
            <TimeInfo colors={colors} fontSize={fontSize} fontWeight={fontWeight}>
              <span>{formatTime(elapsed)}</span>
              <span>/</span>
              <span>{formatTime(currentSong?.duration || 0)}</span>
            </TimeInfo>
          )}
        </StickyFooterTimeInfo>

        {settings.radioName && (
          <RadioName colors={colors} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
            {currentSong?.radioName || 'Radio Name'}
          </RadioName>
        )}

        {settings.liveIndicator && (
          <LiveContainer>
            <LiveIndicator colors={colors} />
            <LiveText colors={colors} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
              {currentSong?.streamerName || 'LIVE'}
            </LiveText>
          </LiveContainer>
        )}

        {settings.timeBar && (
          <StickyFooterTimeBar>
            <TimeBar colors={colors}>
              <TimeBarProgress colors={colors} progress={timeProgress} />
            </TimeBar>
          </StickyFooterTimeBar>
        )}

        {settings.songHistory && showHistoryModal && (
          <HistoryModal
            isOpen={showHistoryModal}
            onClose={() => setShowHistoryModal(false)}
            songHistory={metadata?.song_history}
            colors={colors}
            settings={settings}
            fontSpacing={fontSpacing}
          />
        )}
      </StickyFooterPlayer>
    );
  }

  if (style === 'compact') {
    return (
      <>
        <StickyCompactPlayer colors={colors} dimensions={dimensions}>
          {albumArt !== 'disable' && (
            <CompactAlbumArt settings={{ albumArt }} dimensions={dimensions}>
              {currentSong?.artwork ? (
                <img
                  src={currentSong.artwork}
                  alt="Album Art"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: albumArt
                  }}
                />
              ) : (
                'ALBUM ARTWORK AREA'
              )}
            </CompactAlbumArt>
          )}

          <CompactHeader textAlign={textAlign}>
            {settings.radioName && (
              <RadioName colors={colors} fontSize={fontSize} fontWeight={fontWeight} textAlign={textAlign}>
                {currentSong?.radioName || 'Radio Name'}
              </RadioName>
            )}
            {settings.liveIndicator && (
              <LiveContainer>
                <LiveIndicator colors={colors} />
                <LiveText colors={colors} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
                  {currentSong?.streamerName || 'LIVE'}
                </LiveText>
              </LiveContainer>
            )}
          </CompactHeader>

          <CompactSongInfo textAlign={textAlign}>
            {settings.songName && (
              <SongName colors={colors} fontSize={fontSize} fontWeight={fontWeight} textAlign={textAlign}>
                {currentSong?.title || 'Song Name'}
              </SongName>
            )}
            {settings.artistName && (
              <ArtistName colors={colors} fontSize={fontSize} fontWeight={fontWeight} textAlign={textAlign}>
                {currentSong?.artist || 'Artist Name'}
              </ArtistName>
            )}
          </CompactSongInfo>

          <CompactControls buttonAlign={buttonAlign}>
            {settings.songHistory && (
              <ControlButton
                colors={colors}
                buttonStyle={buttonStyle}
                onClick={() => setShowHistoryModal(true)}
              >
                <FontAwesomeIcon icon={faHistory} />
              </ControlButton>
            )}
            <ControlButton
              colors={colors}
              isPlay
              buttonStyle={buttonStyle}
              onClick={controls.togglePlay}
            >
              <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
            </ControlButton>
            {settings.volumeControl && (
              <ControlsContainer>
                <ControlButton
                  colors={colors}
                  buttonStyle={buttonStyle}
                  onClick={() => setShowVolumeBar(!showVolumeBar)}
                >
                  <FontAwesomeIcon icon={getVolumeIcon()} />
                </ControlButton>
                <VolumeContainer
                  colors={colors}
                  isVisible={showVolumeBar}
                  onMouseLeave={() => setShowVolumeBar(false)}
                >
                  <VolumeSlider
                    colors={colors}
                    value={volume * 100}
                    onChange={handleVolumeChange}
                  />
                </VolumeContainer>
              </ControlsContainer>
            )}
          </CompactControls>

          {settings.time && (
            <CompactTimeInfo colors={colors} fontSize={fontSize} fontWeight={fontWeight}>
              <span>{formatTime(elapsed)}</span>
              <span>{formatTime(currentSong?.duration || 0)}</span>
            </CompactTimeInfo>
          )}

          {settings.timeBar && (
            <CompactTimeBar colors={colors}>
              <TimeBarProgress colors={colors} progress={timeProgress} />
            </CompactTimeBar>
          )}
        </StickyCompactPlayer>

        {settings.songHistory && showHistoryModal && (
          <HistoryModal
            isOpen={showHistoryModal}
            onClose={() => setShowHistoryModal(false)}
            songHistory={metadata?.song_history}
            colors={colors}
            settings={settings}
            fontSpacing={fontSpacing}
          />
        )}
      </>
    );
  }

  return (
    <VerticalPlayer colors={colors} dimensions={dimensions} layoutStyle={style}>
      {style === 'horizontal' ? (
        <>
          <StationInfo>
            {settings.radioName && currentSong?.radioName && (
              <RadioName colors={colors} layoutStyle={style} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
                {currentSong.radioName}
              </RadioName>
            )}
            {settings.liveIndicator && currentSong?.isLive !== undefined && (
              <LiveContainer>
                <LiveIndicator colors={colors} />
                <LiveText colors={colors} layoutStyle={style} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
                  {currentSong.streamerName || 'LIVE'}
                </LiveText>
              </LiveContainer>
            )}
          </StationInfo>
          <HorizontalContainer>
            {albumArt !== 'disable' && (
              <AlbumArtworkContainer layoutStyle={style}>
                <AlbumArtwork settings={{ albumArt }} layoutStyle={style} dimensions={dimensions}>
                  {currentSong?.artwork ? (
                    <img
                      src={currentSong.artwork}
                      alt="Album Art"
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: albumArt || 'cover'
                      }}
                    />
                  ) : (
                    'ALBUM ARTWORK AREA'
                  )}
                </AlbumArtwork>
                {settings.timeBar && (
                  <>
                    <TimeBarContainer layoutStyle={style}>
                      <TimeBar colors={colors}>
                        <TimeBarProgress
                          colors={colors}
                          progress={timeProgress}
                        />
                      </TimeBar>
                    </TimeBarContainer>
                    {settings.time && currentSong && (
                      <TimeInfo colors={colors} layoutStyle={style} albumArtDisabled={albumArt === 'disable'} fontSize={fontSize} fontWeight={fontWeight}>
                        <span>{formatTime(elapsed)}</span>
                        <span>{formatTime(currentSong.duration)}</span>
                      </TimeInfo>
                    )}
                  </>
                )}
              </AlbumArtworkContainer>
            )}
            <PlayerContent layoutStyle={style} albumArtDisabled={albumArt === 'disable'}>
              {(settings.songName || settings.artistName) && currentSong && (
                <SongInfo textAlign={textAlign} colors={colors} layoutStyle={style}>
                  {settings.songName && (
                    <SongName
                      textAlign={textAlign}
                      colors={colors}
                      fontSize={fontSize}
                      fontWeight={fontWeight}
                      fontSpacing={fontSpacing}
                    >
                      {currentSong.title}
                    </SongName>
                  )}
                  {settings.artistName && (
                    <ArtistName
                      textAlign={textAlign}
                      colors={colors}
                      fontSize={fontSize}
                      fontWeight={fontWeight}
                      fontSpacing={fontSpacing}
                    >
                      {currentSong.artist}
                    </ArtistName>
                  )}
                </SongInfo>
              )}
              <ControlsContainer>
                <Controls layoutStyle={style} buttonAlign={buttonAlign}>
                  {settings.songHistory && (
                    <ControlButton
                      colors={colors}
                      buttonStyle={buttonStyle}
                      onClick={() => setShowHistoryModal(true)}
                    >
                      {buttonStyle === 'text' ? (
                        'History'
                      ) : (
                        <FontAwesomeIcon icon={faHistory} />
                      )}
                    </ControlButton>
                  )}
                  <ControlButton
                    colors={colors}
                    isPlay
                    buttonStyle={buttonStyle}
                    onClick={controls.togglePlay}
                  >
                    {buttonStyle === 'text' ? (
                      isPlaying ? 'Pause' : 'Play'
                    ) : (
                      <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                    )}
                  </ControlButton>
                  {settings.volumeControl && (
                    <ControlButton
                      colors={colors}
                      buttonStyle={buttonStyle}
                      onClick={() => setShowVolumeBar(!showVolumeBar)}
                    >
                      {buttonStyle === 'text' ? (
                        'Volume'
                      ) : (
                        <FontAwesomeIcon icon={getVolumeIcon()} />
                      )}
                    </ControlButton>
                  )}
                </Controls>
                {settings.volumeControl && (
                  <VolumeContainer
                    colors={colors}
                    isVisible={showVolumeBar}
                    onMouseLeave={() => setShowVolumeBar(false)}
                  >
                    <VolumeSlider
                      colors={colors}
                      value={volume * 100}
                      onChange={handleVolumeChange}
                    />
                  </VolumeContainer>
                )}
              </ControlsContainer>
              {albumArt === 'disable' && settings.timeBar && (
                <>
                  {settings.time && currentSong && (
                    <TimeInfo colors={colors} layoutStyle={style} albumArtDisabled fontSize={fontSize} fontWeight={fontWeight}>
                      <span>{formatTime(elapsed)}</span>
                      <span>{formatTime(currentSong.duration)}</span>
                    </TimeInfo>
                  )}
                  <TimeBarContainer layoutStyle={style} albumArtDisabled>
                    <TimeBar colors={colors}>
                      <TimeBarProgress
                        colors={colors}
                        progress={timeProgress}
                      />
                    </TimeBar>
                  </TimeBarContainer>
                </>
              )}
            </PlayerContent>
          </HorizontalContainer>
        </>
      ) : (
        <>
          <AlbumArtwork settings={{ albumArt }} layoutStyle={style} dimensions={dimensions}>
            {albumArt !== 'disable' && (
              currentSong?.artwork ? (
                <img
                  src={currentSong.artwork}
                  alt="Album Art"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: albumArt || 'cover'
                  }}
                />
              ) : (
                'ALBUM ARTWORK AREA'
              )
            )}
          </AlbumArtwork>

          {style === 'square' && (
            <StationInfo layoutStyle={style}>
              {settings.radioName && currentSong?.radioName && (
                <RadioName colors={colors} layoutStyle={style} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
                  {currentSong.radioName}
                </RadioName>
              )}
              {settings.liveIndicator && currentSong?.isLive !== undefined && (
                <LiveContainer layoutStyle={style}>
                  <LiveIndicator colors={colors} />
                  <LiveText colors={colors} layoutStyle={style} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
                    {currentSong.streamerName || 'LIVE'}
                  </LiveText>
                </LiveContainer>
              )}
            </StationInfo>
          )}

          <PlayerContent layoutStyle={style}>
            {style === 'vertical' && settings.timeBar && (
              <TimeBarContainer>
                <TimeBar colors={colors}>
                  <TimeBarProgress
                    colors={colors}
                    progress={timeProgress}
                  />
                </TimeBar>
              </TimeBarContainer>
            )}

            {style === 'vertical' && settings.time && currentSong && (
              <TimeInfo colors={colors} layoutStyle={style} fontSize={fontSize} fontWeight={fontWeight}>
                <span>{formatTime(elapsed)}</span>
                <span>{formatTime(currentSong.duration)}</span>
              </TimeInfo>
            )}

            {style === 'vertical' && (
              <StationInfo>
                {settings.radioName && currentSong?.radioName && (
                  <RadioName colors={colors} layoutStyle={style} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
                    {currentSong.radioName}
                  </RadioName>
                )}
                {settings.liveIndicator && currentSong?.isLive !== undefined && (
                  <LiveContainer>
                    <LiveIndicator colors={colors} />
                    <LiveText colors={colors} layoutStyle={style} fontSize={fontSize} fontWeight={fontWeight} fontSpacing={fontSpacing}>
                      {currentSong.streamerName || 'LIVE'}
                    </LiveText>
                  </LiveContainer>
                )}
              </StationInfo>
            )}

            {(settings.songName || settings.artistName) && currentSong && (
              <SongInfo textAlign={textAlign} colors={colors} layoutStyle={style}>
                {settings.songName && (
                  <SongName
                    textAlign={textAlign}
                    colors={colors}
                    fontSize={fontSize}
                    fontWeight={fontWeight}
                    fontSpacing={fontSpacing}
                  >
                    {currentSong.title}
                  </SongName>
                )}
                {settings.artistName && (
                  <ArtistName
                    textAlign={textAlign}
                    colors={colors}
                    fontSize={fontSize}
                    fontWeight={fontWeight}
                    fontSpacing={fontSpacing}
                  >
                    {currentSong.artist}
                  </ArtistName>
                )}
              </SongInfo>
            )}

            <ControlsContainer>
              <Controls layoutStyle={style} buttonAlign={buttonAlign}>
                {settings.songHistory && (
                  <ControlButton
                    colors={colors}
                    buttonStyle={buttonStyle}
                    onClick={() => setShowHistoryModal(true)}
                  >
                    {buttonStyle === 'text' ? (
                      'History'
                    ) : (
                      <FontAwesomeIcon icon={faHistory} />
                    )}
                  </ControlButton>
                )}
                <ControlButton
                  colors={colors}
                  isPlay
                  buttonStyle={buttonStyle}
                  onClick={controls.togglePlay}
                >
                  {buttonStyle === 'text' ? (
                    isPlaying ? 'Pause' : 'Play'
                  ) : (
                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} />
                  )}
                </ControlButton>
                {settings.volumeControl && (
                  <ControlButton
                    colors={colors}
                    buttonStyle={buttonStyle}
                    onClick={() => setShowVolumeBar(!showVolumeBar)}
                  >
                    {buttonStyle === 'text' ? (
                      'Volume'
                    ) : (
                      <FontAwesomeIcon icon={getVolumeIcon()} />
                    )}
                  </ControlButton>
                )}
              </Controls>

              {settings.volumeControl && (
                <VolumeContainer
                  colors={colors}
                  isVisible={showVolumeBar}
                  onMouseLeave={() => setShowVolumeBar(false)}
                >
                  <VolumeSlider
                    colors={colors}
                    value={volume * 100}
                    onChange={handleVolumeChange}
                  />
                </VolumeContainer>
              )}
            </ControlsContainer>
          </PlayerContent>

          {style === 'square' && settings.timeBar && (
            <>
              <TimeBarContainer layoutStyle={style}>
                <TimeBar colors={colors} layoutStyle={style}>
                  <TimeBarProgress
                    colors={colors}
                    layoutStyle={style}
                    progress={timeProgress}
                  />
                </TimeBar>
              </TimeBarContainer>
              {settings.time && currentSong && (
                <TimeInfo colors={colors} layoutStyle={style} fontSize={fontSize} fontWeight={fontWeight}>
                  <span>{formatTime(elapsed)}</span>
                  <span>{formatTime(currentSong.duration)}</span>
                </TimeInfo>
              )}
            </>
          )}
        </>
      )}

      {settings.songHistory && showHistoryModal && (
        <HistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          songHistory={metadata?.song_history}
          colors={colors}
          settings={settings}
          fontSpacing={fontSpacing}
        />
      )}
    </VerticalPlayer>
  );
};

export default PlayerPreview;